// Elbow Joint Template Configuration
// Questions simplified to 12-year-old reading level

export const elbowTemplate = {
  id: 'ELBOW',
  name: 'Elbow',
  icon: '💪',
  dominantSide: 'hand',

  fields: {
    dominantHand: {
      type: 'radio',
      label: 'Which is your dominant hand (the one you write with)?',
      options: ['Right', 'Left'],
      required: true
    },

    injuredSide: {
      type: 'radio',
      label: 'Which elbow has the problem?',
      options: ['Right', 'Left', 'Both'],
      required: true
    },

    problemDate: {
      type: 'date',
      label: 'When did your elbow problem start?',
      required: true
    },

    onsetType: {
      type: 'radio',
      label: 'How did it start?',
      options: ['Suddenly', 'Slowly over time'],
      required: true
    },

    mainProblem: {
      type: 'checkbox-multiple',
      label: 'What problems are you having? (tick all that apply)',
      options: [
        'Pain',
        'Swelling',
        'Stiffness',
        'Weakness',
        'Locking or catching',
        'Tingling or numbness in hand',
        'Other'
      ]
    },

    mainProblemOther: {
      type: 'text',
      label: 'Please describe the other problem:',
      placeholder: 'Tell us about your other problem',
      showWhen: { field: 'mainProblem', includes: 'Other' }
    },

    problemDescription: {
      type: 'textarea',
      label: 'Tell us what happened:',
      placeholder: 'Describe how your elbow problem started',
      rows: 3,
      required: true
    },

    // Pain Section
    painLocation: {
      type: 'checkbox-multiple',
      label: 'Where exactly is your pain?',
      options: [
        'Inside of elbow (closest to body)',
        'Outside of elbow',
        'Back of elbow',
        'Front of elbow',
        'All around the elbow'
      ]
    },

    painType: {
      type: 'checkbox-multiple',
      label: 'What does your pain feel like?',
      options: ['Aching', 'Sharp', 'Throbbing', 'Burning', 'Other']
    },

    painTypeOther: {
      type: 'text',
      label: 'Please describe the pain:',
      placeholder: 'Tell us what your pain feels like',
      showWhen: { field: 'painType', includes: 'Other' }
    },

    painSeverity: {
      type: 'radio',
      label: 'How bad is your pain? (0 = no pain, 10 = worst pain ever)',
      options: ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '10']
    },

    painRadiation: {
      type: 'radio',
      label: 'Does the pain spread anywhere else?',
      options: ['Yes, to my forearm', 'Yes, to my wrist/hand', 'Yes, to my shoulder', 'No, just the elbow']
    },

    painTiming: {
      type: 'checkbox-multiple',
      label: 'When is your pain worst?',
      options: [
        'All the time',
        'During activity',
        'After activity',
        'At night',
        'In the morning'
      ]
    },

    painWorsening: {
      type: 'checkbox-multiple',
      label: 'What makes your pain worse?',
      options: [
        'Gripping things',
        'Lifting',
        'Pushing',
        'Pulling',
        'Twisting (like opening a jar)',
        'Throwing',
        'Typing',
        'Other'
      ]
    },

    painWorseningOther: {
      type: 'text',
      label: 'What else makes your pain worse?',
      placeholder: 'Tell us what else makes your pain worse',
      showWhen: { field: 'painWorsening', includes: 'Other' }
    },

    painRelieving: {
      type: 'checkbox-multiple',
      label: 'What helps your pain feel better?',
      options: [
        'Rest',
        'Ice',
        'Heat',
        'Pain medication',
        'Wearing a brace/support',
        'Other'
      ]
    },

    painRelievingOther: {
      type: 'text',
      label: 'What else helps your pain?',
      placeholder: 'Tell us what else helps your pain',
      showWhen: { field: 'painRelieving', includes: 'Other' }
    },

    // Swelling Section
    hasSwelling: {
      type: 'radio',
      label: 'Is your elbow swollen?',
      options: ['Yes', 'No']
    },

    swellingLocation: {
      type: 'text',
      label: 'Where is the swelling?',
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingChange: {
      type: 'radio',
      label: 'Is the swelling:',
      options: ['Getting worse', 'Getting better', 'Staying the same'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    swellingWithActivity: {
      type: 'radio',
      label: 'Does the swelling get worse with activity?',
      options: ['Yes', 'No'],
      showWhen: { field: 'hasSwelling', equals: 'Yes' }
    },

    // Stiffness Section
    hasStiffness: {
      type: 'radio',
      label: 'Is your elbow stiff?',
      options: ['Yes', 'No']
    },

    stiffnessMovements: {
      type: 'checkbox-multiple',
      label: 'Which movements are stiff or limited?',
      options: [
        'Bending (flexion)',
        'Straightening (extension)',
        'Turning palm up',
        'Turning palm down'
      ],
      showWhen: { field: 'hasStiffness', equals: 'Yes' }
    },

    lockingCatching: {
      type: 'radio',
      label: 'Does your elbow lock or catch?',
      options: ['Yes', 'No']
    },

    lockingDescription: {
      type: 'text',
      label: 'Tell us more about the locking or catching:',
      showWhen: { field: 'lockingCatching', equals: 'Yes' }
    },

    // Instability
    givingWay: {
      type: 'radio',
      label: 'Does your elbow feel unstable or give way?',
      options: ['Yes', 'No']
    },

    previousDislocation: {
      type: 'radio',
      label: 'Has your elbow ever dislocated (popped out)?',
      options: ['Yes', 'No']
    },

    // Nerve Symptoms
    tinglingNumbness: {
      type: 'radio',
      label: 'Do you have tingling or numbness in your hand or fingers?',
      options: ['Yes', 'No']
    },

    tinglingLocation: {
      type: 'checkbox-multiple',
      label: 'Which fingers feel tingly or numb?',
      options: [
        'Little finger and ring finger',
        'Thumb, index, and middle fingers',
        'All fingers'
      ],
      showWhen: { field: 'tinglingNumbness', equals: 'Yes' }
    },

    // Functional Limitations
    eatingDifficulty: {
      type: 'radio',
      label: 'Do you have trouble eating with that arm?',
      options: ['Yes', 'No']
    },

    dressingDifficulty: {
      type: 'radio',
      label: 'Do you have trouble dressing yourself?',
      options: ['Yes', 'No']
    },

    liftingDifficulty: {
      type: 'radio',
      label: 'Can you lift things normally?',
      options: ['Yes', 'No, it\'s difficult', 'No, I can\'t lift at all']
    },

    writingDifficulty: {
      type: 'radio',
      label: 'Do you have trouble writing or using a computer?',
      options: ['Yes', 'No']
    },

    sportDifficulty: {
      type: 'radio',
      label: 'Can you do your sports or hobbies?',
      options: ['Yes, no problem', 'Yes, but it hurts', 'No']
    },

    // Previous History
    previousElbowProblems: {
      type: 'radio',
      label: 'Have you had elbow problems before?',
      options: ['Yes', 'No']
    },

    previousElbowDetails: {
      type: 'textarea',
      label: 'Tell us about your previous elbow problems:',
      rows: 2,
      showWhen: { field: 'previousElbowProblems', equals: 'Yes' }
    },

    previousElbowSurgery: {
      type: 'radio',
      label: 'Have you had elbow surgery before?',
      options: ['Yes', 'No']
    },

    repetitiveUse: {
      type: 'radio',
      label: 'Does your job or hobby involve a lot of repetitive arm movements?',
      options: ['Yes', 'No']
    },

    repetitiveUseDetails: {
      type: 'text',
      label: 'What kind of repetitive movements?',
      placeholder: 'e.g., typing, painting, using tools',
      showWhen: { field: 'repetitiveUse', equals: 'Yes' }
    },

    // Medical History
    medicalConditions: {
      type: 'checkbox-multiple',
      label: 'Do you have any of these conditions?',
      options: [
        'Arthritis',
        'Gout',
        'Rheumatoid arthritis',
        'Diabetes',
        'None of these'
      ]
    },

    currentMedications: {
      type: 'textarea',
      label: 'What medications do you take?',
      placeholder: 'List all your current medications',
      rows: 2
    },

    allergies: {
      type: 'text',
      label: 'Do you have any allergies?',
      placeholder: 'List any allergies'
    },

    // Lifestyle
    occupation: {
      type: 'text',
      label: 'What is your job?',
      placeholder: 'Tell us what you do for work'
    },

    sport: {
      type: 'text',
      label: 'What sports or hobbies do you do?',
      placeholder: 'e.g., tennis, golf, weightlifting, guitar'
    },

    smoking: {
      type: 'radio',
      label: 'Do you smoke?',
      options: ['No, never smoked', 'I used to smoke', 'Yes, I smoke']
    }
  },

  sections: [
    {
      id: 'consent',
      title: '0. Consent & Agreements',
      component: 'ConsentSection',
      enabled: true
    },
    {
      id: 'demographics',
      title: '1. Your Details',
      component: 'DemographicsSection',
      enabled: true
    },
    {
      id: 'problem',
      title: '2. About Your Elbow Problem',
      component: 'GenericSection',
      enabled: true,
      fields: ['dominantHand', 'injuredSide', 'problemDate', 'onsetType', 'mainProblem', 'mainProblemOther', 'problemDescription']
    },
    {
      id: 'pain',
      title: '3. Your Pain',
      component: 'GenericSection',
      enabled: true,
      fields: ['painLocation', 'painType', 'painTypeOther', 'painSeverity', 'painRadiation', 'painTiming', 'painWorsening', 'painWorseningOther', 'painRelieving', 'painRelievingOther']
    },
    {
      id: 'swelling',
      title: '4. Swelling',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasSwelling', 'swellingLocation', 'swellingChange', 'swellingWithActivity']
    },
    {
      id: 'movement',
      title: '5. Movement & Stability',
      component: 'GenericSection',
      enabled: true,
      fields: ['hasStiffness', 'stiffnessMovements', 'lockingCatching', 'lockingDescription', 'givingWay', 'previousDislocation']
    },
    {
      id: 'nerves',
      title: '6. Nerve Symptoms',
      component: 'GenericSection',
      enabled: true,
      fields: ['tinglingNumbness', 'tinglingLocation']
    },
    {
      id: 'functional',
      title: '7. What You Can & Can\'t Do',
      component: 'GenericSection',
      enabled: true,
      fields: ['eatingDifficulty', 'dressingDifficulty', 'liftingDifficulty', 'writingDifficulty', 'sportDifficulty']
    },
    {
      id: 'history',
      title: '8. Previous Problems',
      component: 'GenericSection',
      enabled: true,
      fields: ['previousElbowProblems', 'previousElbowDetails', 'previousElbowSurgery', 'repetitiveUse', 'repetitiveUseDetails']
    },
    {
      id: 'medical',
      title: '9. Your Health & Lifestyle',
      component: 'MedicalHistorySection',
      enabled: true
    },    {
      id: 'exerciseVitalSign',
      title: '10. Exercise as a Vital Sign',
      component: 'ExerciseVitalSignSection',
      enabled: true
    },

    {
      id: 'goals',
      title: '11. Your Goals',
      component: 'GoalsSection',
      enabled: true
    }
  ],

  letterTemplate: {
    mechanismLanguage: {
      'Suddenly': 'acute onset',
      'Slowly over time': 'gradual onset'
    },
    commonDiagnoses: [
      'Lateral epicondylitis (tennis elbow)',
      'Medial epicondylitis (golfer\'s elbow)',
      'Olecranon bursitis',
      'Cubital tunnel syndrome',
      'Elbow osteoarthritis',
      'Distal biceps tendinopathy',
      'Triceps tendinopathy'
    ]
  }
};

export default elbowTemplate;
